import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  DollarSign, MousePointerClick, Eye, 
  Target, AlertCircle, FileSpreadsheet, Search, EyeOff, Calendar,
  TrendingUp, TrendingDown, Upload
} from "lucide-react";
import axios from "axios";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import PageDateFilter from "@/components/PageDateFilter";

// Nav menu dark color for chart text
const NAV_DARK_COLOR = "#0F172A";

// Currency symbols
const CURRENCY_SYMBOLS = {
  TRY: "₺",
  USD: "$",
  EUR: "€",
  GBP: "£"
};

const GoogleAdsPage = () => {
  const { sessionToken, isMasterAdmin } = useAuth();
  const { chartColors, pageTitles } = useBranding();
  const { isPresentationMode, maskNumber, maskAmount } = usePresentationMode();
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const [dateFilterInitialized, setDateFilterInitialized] = useState(false);
  const [adsData, setAdsData] = useState(null);
  const [error, setError] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState("USD");
  const [exchangeRates, setExchangeRates] = useState({ TRY: 1, USD: 0.029, EUR: 0.027, GBP: 0.023 });
  const [displaySettings, setDisplaySettings] = useState({ show_conversions: true });

  // Targeted keywords state
  const [targetedKeywords, setTargetedKeywords] = useState(null);
  const [loadingTargeted, setLoadingTargeted] = useState(false);
  
  // Last upload date state
  const [lastUploadDate, setLastUploadDate] = useState(null);

  // Comparison state
  const [comparisonMode, setComparisonMode] = useState("prior"); // "none", "prior", "yoy"
  const [comparisonData, setComparisonData] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);

  // CRITICAL: Track tenant ID in state to trigger re-fetches when tenant changes
  const [currentTenantId, setCurrentTenantId] = useState(() => sessionStorage.getItem("viewing_tenant_id"));
  
  // Listen for tenant changes
  useEffect(() => {
    const checkTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (newTenantId !== currentTenantId) {
        console.log("=== GoogleAdsPage: TENANT CHANGED ===", currentTenantId, "->", newTenantId);
        setCurrentTenantId(newTenantId);
      }
    };
    const interval = setInterval(checkTenantChange, 500);
    return () => clearInterval(interval);
  }, [currentTenantId]);

  // Fetch live exchange rates on mount
  useEffect(() => {
    const fetchRates = async () => {
      try {
        // Using exchangerate-api (free tier)
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/TRY');
        if (response.ok) {
          const data = await response.json();
          setExchangeRates({
            TRY: 1,
            USD: data.rates.USD || 0.029,
            EUR: data.rates.EUR || 0.027,
            GBP: data.rates.GBP || 0.023
          });
          console.log("Live exchange rates loaded:", data.rates);
        }
      } catch (err) {
        console.log("Using fallback exchange rates");
      }
    };
    fetchRates();
  }, []);

  // Fetch display settings
  useEffect(() => {
    const fetchDisplaySettings = async () => {
      try {
        const viewingTenantId = currentTenantId;
        if (viewingTenantId) {
          const response = await axios.get(`${API}/tenants/${viewingTenantId}/display-settings`, {
            params: { Authorization: `Bearer ${sessionToken}` }
          });
          setDisplaySettings(response.data);
        }
      } catch (err) {
        console.log("Using default display settings");
      }
    };
    if (sessionToken) fetchDisplaySettings();
  }, [sessionToken, currentTenantId]); // CRITICAL: Added currentTenantId

  // Toggle conversions visibility (super admin only)
  const toggleConversions = async () => {
    try {
      const viewingTenantId = currentTenantId;
      if (viewingTenantId) {
        const newValue = !displaySettings.show_conversions;
        await axios.put(`${API}/tenants/${viewingTenantId}/display-settings`, 
          { show_conversions: newValue },
          { params: { Authorization: `Bearer ${sessionToken}` } }
        );
        setDisplaySettings(prev => ({ ...prev, show_conversions: newValue }));
      }
    } catch (err) {
      console.error("Failed to toggle conversions:", err);
    }
  };

  const handleDateChange = useCallback((dateInfo) => {
    console.log("Google Ads date filter changed:", dateInfo);
    setDateFilter(dateInfo);
    setDateFilterInitialized(true);
  }, []);

  const fetchAdsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      if (dateFilter?.startDate) params.start_date = dateFilter.startDate;
      if (dateFilter?.endDate) params.end_date = dateFilter.endDate;

      console.log("=== GoogleAdsPage fetchAdsData ===", "tenantId:", viewingTenantId);
      const response = await axios.get(`${API}/integrations/google-ads/data`, { params });
      console.log("Google Ads response:", response.data);
      setAdsData(response.data);
    } catch (error) {
      console.error("Error fetching Google Ads data:", error);
      if (error.response?.status === 400) {
        setError("No Google Ads data found. Please ask a super admin to upload campaign data.");
      } else {
        setError("Failed to load Google Ads data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!dateFilterInitialized) return;
    fetchAdsData();
  }, [sessionToken, dateFilterInitialized, dateFilter?.startDate, dateFilter?.endDate, currentTenantId]); // CRITICAL: Added currentTenantId

  // Fetch targeted keywords
  const fetchTargetedKeywords = async () => {
    setLoadingTargeted(true);
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/google-ads/targeted-keywords`, { params });
      setTargetedKeywords(response.data);
    } catch (error) {
      console.error("Error fetching targeted keywords:", error);
      setTargetedKeywords(null);
    } finally {
      setLoadingTargeted(false);
    }
  };

  useEffect(() => {
    if (!sessionToken) return;
    fetchTargetedKeywords();
  }, [sessionToken, currentTenantId]);

  // Fetch last upload date
  const fetchLastUploadDate = useCallback(async () => {
    try {
      const viewingTenantId = currentTenantId;
      const params = { Authorization: `Bearer ${sessionToken}` };
      if (viewingTenantId) {
        params["X-Tenant-ID"] = viewingTenantId;
      }
      const response = await axios.get(`${API}/integrations/google-ads/last-upload-date`, { params });
      setLastUploadDate(response.data);
    } catch (error) {
      console.error("Error fetching last upload date:", error);
      setLastUploadDate(null);
    }
  }, [sessionToken, currentTenantId]);

  useEffect(() => {
    if (!sessionToken) return;
    fetchLastUploadDate();
  }, [sessionToken, currentTenantId, fetchLastUploadDate]);

  // Calculate comparison period dates
  const getComparisonDates = useCallback(() => {
    if (!dateFilter?.startDate || !dateFilter?.endDate || comparisonMode === "none") return null;
    
    const start = new Date(dateFilter.startDate);
    const end = new Date(dateFilter.endDate);
    
    if (comparisonMode === "yoy") {
      const prevStart = new Date(start);
      prevStart.setFullYear(prevStart.getFullYear() - 1);
      const prevEnd = new Date(end);
      prevEnd.setFullYear(prevEnd.getFullYear() - 1);
      return {
        startDate: prevStart.toISOString().split('T')[0],
        endDate: prevEnd.toISOString().split('T')[0]
      };
    }
    
    // Prior period
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - daysDiff + 1);
    return {
      startDate: prevStart.toISOString().split('T')[0],
      endDate: prevEnd.toISOString().split('T')[0]
    };
  }, [dateFilter?.startDate, dateFilter?.endDate, comparisonMode]);

  // Fetch comparison period data
  useEffect(() => {
    const compDates = getComparisonDates();
    if (!compDates || !sessionToken || !dateFilterInitialized) {
      setComparisonData(null);
      return;
    }

    const fetchComparison = async () => {
      setLoadingComparison(true);
      try {
        const viewingTenantId = currentTenantId;
        const params = { Authorization: `Bearer ${sessionToken}` };
        if (viewingTenantId) params["X-Tenant-ID"] = viewingTenantId;
        params.start_date = compDates.startDate;
        params.end_date = compDates.endDate;

        const response = await axios.get(`${API}/integrations/google-ads/data`, { params });
        setComparisonData(response.data);
      } catch (err) {
        console.log("No comparison data available");
        setComparisonData(null);
      } finally {
        setLoadingComparison(false);
      }
    };

    fetchComparison();
  }, [sessionToken, dateFilterInitialized, dateFilter?.startDate, dateFilter?.endDate, comparisonMode, currentTenantId, getComparisonDates]);

  // Calculate percent change between current and comparison
  const calcChange = (current, previous) => {
    if (previous === null || previous === undefined) return null;
    if (previous === 0 && current === 0) return "0.0";
    if (previous === 0) return null; // Can't calculate % from 0
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Check if comparison data is available
  const hasComparisonData = comparisonMode !== "none" && comparisonData?.summary && (
    comparisonData.summary.total_impressions > 0 || 
    comparisonData.summary.total_clicks > 0 || 
    comparisonData.summary.total_cost > 0
  );
  const noComparisonDataReason = comparisonMode !== "none" && !hasComparisonData && !loadingComparison
    ? "No data for comparison period"
    : null;

  const comparisonLabel = comparisonMode === "yoy" ? "vs year ago" : "vs prior";

  const summary = adsData?.summary || {};
  const campaigns = adsData?.campaigns || [];
  const keywords = adsData?.keywords || [];
  const dailyData = adsData?.daily_data || [];
  const sourceCurrency = adsData?.currency || "TRY";

  // Convert cost from source currency (TRY) to display currency
  const convertCurrency = (value) => {
    if (!value) return 0;
    if (displayCurrency === "TRY") return value;
    return value * (exchangeRates[displayCurrency] || 0.029);
  };

  const formatCurrency = (val) => {
    const converted = convertCurrency(val);
    const symbol = CURRENCY_SYMBOLS[displayCurrency];
    return `${symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined) return "0";
    return Math.round(val).toLocaleString();
  };
  
  const formatPercent = (val) => {
    if (val === null || val === undefined) return "0.00%";
    return `${val.toFixed(2)}%`;
  };

  // Format upload date for display
  const formatUploadDate = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return null;
    }
  };

  // Show header while waiting for date filter to initialize
  if (!dateFilterInitialized) {
    return (
      <div className="animate-fade-in" data-testid="google-ads-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">{pageTitles?.google_ads || "Google Ads"}</h1>
            <p className="text-sm text-slate-500 mt-1">Campaign and keyword performance</p>
          </div>
          <PageDateFilter pageId="google-ads" defaultPreset="last_30_days" onDateChange={handleDateChange} />
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="google-ads-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Google Ads</h1>
          <p className="text-sm text-slate-500 mt-1">
            Campaign and keyword performance
            {adsData?.record_count && (
              <span className="ml-2 text-xs bg-slate-100 px-2 py-1 rounded">
                {adsData.record_count.campaigns} campaigns • {adsData.record_count.keywords} keywords
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={comparisonMode} onValueChange={setComparisonMode}>
            <SelectTrigger className="w-[160px]" data-testid="ads-comparison-mode">
              <SelectValue placeholder="Comparison" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Comparison</SelectItem>
              <SelectItem value="prior">vs Prior Period</SelectItem>
              <SelectItem value="yoy">vs Year Ago</SelectItem>
            </SelectContent>
          </Select>
          <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
            <SelectTrigger className="w-24">
              <SelectValue placeholder="Currency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="USD">USD ($)</SelectItem>
              <SelectItem value="TRY">TRY (₺)</SelectItem>
              <SelectItem value="EUR">EUR (€)</SelectItem>
              <SelectItem value="GBP">GBP (£)</SelectItem>
            </SelectContent>
          </Select>
          <PageDateFilter pageId="google-ads" defaultPreset="last_30_days" onDateChange={handleDateChange} />
        </div>
      </div>

      {/* Data Freshness Indicator */}
      {lastUploadDate && (lastUploadDate.data_through || lastUploadDate.last_upload_date) && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between" data-testid="data-freshness-banner">
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              {lastUploadDate.data_through ? (
                <>
                  <strong>Data through:</strong> {lastUploadDate.data_through}
                </>
              ) : (
                <>
                  <strong>Last uploaded:</strong> {formatUploadDate(lastUploadDate.last_upload_date)}
                </>
              )}
            </p>
          </div>
          <span className="text-xs text-blue-500">
            Uploaded {formatUploadDate(lastUploadDate.last_upload_date)}
          </span>
        </div>
      )}

      {/* Manual Upload Note */}
      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <p className="text-sm text-amber-800">
          <strong>Note:</strong> Google Ads data is uploaded manually and typically refreshed on <strong>Wednesdays</strong>.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : error || (!adsData?.campaigns?.length && !adsData?.keywords?.length) ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium mb-2">No Google Ads Data</h3>
            <p className="text-slate-500 text-sm mb-4">
              Google Ads data is managed by super admins through the Admin Panel.
            </p>
            <div className="text-xs text-slate-400">
              <p>Contact your administrator to upload campaign and keyword data.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Super Admin Toggle for Conversions */}
          {isMasterAdmin && (
            <div className="flex justify-end mb-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleConversions}
                className="text-xs"
              >
                {displaySettings.show_conversions ? (
                  <><EyeOff className="w-3 h-3 mr-1" /> Hide Conversions</>
                ) : (
                  <><Eye className="w-3 h-3 mr-1" /> Show Conversions</>
                )}
              </Button>
            </div>
          )}

          {/* KPI Cards */}
          <div className={`grid grid-cols-2 ${displaySettings.show_conversions ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 mb-6`}>
            {/* Impressions Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Impressions</p>
                    <p className="text-2xl font-bold">{formatNumber(summary.total_impressions || 0)}</p>
                    {comparisonMode !== "none" && (
                      hasComparisonData ? (() => {
                        const change = calcChange(summary.total_impressions, comparisonData.summary.total_impressions);
                        if (change === null) return <span className="text-xs text-slate-400 mt-1 block">N/A {comparisonLabel}</span>;
                        const isUp = parseFloat(change) > 0;
                        const isDown = parseFloat(change) < 0;
                        return (
                          <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${isUp ? "text-emerald-600" : isDown ? "text-red-500" : "text-slate-500"}`}>
                            {isUp && <TrendingUp className="w-3 h-3" />}
                            {isDown && <TrendingDown className="w-3 h-3" />}
                            <span>{isUp && "+"}{change}% {comparisonLabel}</span>
                          </div>
                        );
                      })()
                      : loadingComparison 
                        ? <span className="text-xs text-slate-400 mt-1 block animate-pulse">Loading...</span>
                        : <span className="text-xs text-slate-400 mt-1 block">No data {comparisonLabel}</span>
                    )}
                  </div>
                  <Eye className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            {/* Clicks Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Clicks</p>
                    <p className="text-2xl font-bold">{formatNumber(summary.total_clicks || 0)}</p>
                    <p className="text-xs text-slate-400">{formatPercent(summary.avg_ctr)} CTR</p>
                    {comparisonMode !== "none" && (
                      hasComparisonData ? (() => {
                        const change = calcChange(summary.total_clicks, comparisonData.summary.total_clicks);
                        if (change === null) return <span className="text-xs text-slate-400 mt-1 block">N/A {comparisonLabel}</span>;
                        const isUp = parseFloat(change) > 0;
                        const isDown = parseFloat(change) < 0;
                        return (
                          <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${isUp ? "text-emerald-600" : isDown ? "text-red-500" : "text-slate-500"}`}>
                            {isUp && <TrendingUp className="w-3 h-3" />}
                            {isDown && <TrendingDown className="w-3 h-3" />}
                            <span>{isUp && "+"}{change}% {comparisonLabel}</span>
                          </div>
                        );
                      })()
                      : loadingComparison 
                        ? <span className="text-xs text-slate-400 mt-1 block animate-pulse">Loading...</span>
                        : <span className="text-xs text-slate-400 mt-1 block">No data {comparisonLabel}</span>
                    )}
                  </div>
                  <MousePointerClick className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            {/* Spend Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Spend</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary.total_cost || 0)}</p>
                    <p className="text-xs text-slate-400">{formatCurrency(summary.avg_cpc || 0)} avg CPC</p>
                    {comparisonMode !== "none" && (
                      hasComparisonData ? (() => {
                        const change = calcChange(summary.total_cost, comparisonData.summary.total_cost);
                        if (change === null) return <span className="text-xs text-slate-400 mt-1 block">N/A {comparisonLabel}</span>;
                        const isUp = parseFloat(change) > 0;
                        const isDown = parseFloat(change) < 0;
                        return (
                          <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${isUp ? "text-emerald-600" : isDown ? "text-red-500" : "text-slate-500"}`}>
                            {isUp && <TrendingUp className="w-3 h-3" />}
                            {isDown && <TrendingDown className="w-3 h-3" />}
                            <span>{isUp && "+"}{change}% {comparisonLabel}</span>
                          </div>
                        );
                      })()
                      : loadingComparison 
                        ? <span className="text-xs text-slate-400 mt-1 block animate-pulse">Loading...</span>
                        : <span className="text-xs text-slate-400 mt-1 block">No data {comparisonLabel}</span>
                    )}
                  </div>
                  <DollarSign className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            {/* Conversions Card */}
            {displaySettings.show_conversions && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Conversions</p>
                      <p className="text-2xl font-bold">{formatNumber(summary.total_conversions || 0)}</p>
                      {comparisonMode !== "none" && (
                        hasComparisonData ? (() => {
                          const change = calcChange(summary.total_conversions, comparisonData.summary.total_conversions);
                          if (change === null) return <span className="text-xs text-slate-400 mt-1 block">N/A {comparisonLabel}</span>;
                          const isUp = parseFloat(change) > 0;
                          const isDown = parseFloat(change) < 0;
                          return (
                            <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${isUp ? "text-emerald-600" : isDown ? "text-red-500" : "text-slate-500"}`}>
                              {isUp && <TrendingUp className="w-3 h-3" />}
                              {isDown && <TrendingDown className="w-3 h-3" />}
                              <span>{isUp && "+"}{change}% {comparisonLabel}</span>
                            </div>
                          );
                        })()
                        : loadingComparison 
                          ? <span className="text-xs text-slate-400 mt-1 block animate-pulse">Loading...</span>
                          : <span className="text-xs text-slate-400 mt-1 block">No data {comparisonLabel}</span>
                      )}
                    </div>
                    <Target className="w-8 h-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Tabs for Overview / Campaigns / Keywords */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="campaigns">Campaigns ({campaigns.length})</TabsTrigger>
              <TabsTrigger value="keywords">Keywords ({keywords.length})</TabsTrigger>
              <TabsTrigger value="targeted">
                Targeted Keywords {targetedKeywords?.total_keywords > 0 && `(${targetedKeywords.total_keywords})`}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Spend Over Time */}
                <Card>
                  <CardHeader>
                    <CardTitle style={{ color: NAV_DARK_COLOR }}>Daily Spend ({displayCurrency})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dailyData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={dailyData.map(d => ({ ...d, cost: convertCurrency(d.cost) }))}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: NAV_DARK_COLOR }} angle={-45} textAnchor="end" height={60} />
                          <YAxis tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} tickFormatter={(v) => `${CURRENCY_SYMBOLS[displayCurrency]}${maskNumber(v)}`} />
                          <Tooltip formatter={(v) => [`${CURRENCY_SYMBOLS[displayCurrency]}${v.toFixed(2)}`, "Spend"]} contentStyle={{ color: NAV_DARK_COLOR }} />
                          <Line type="monotone" dataKey="cost" stroke={chartColors?.[0] || "#00A5A8"} strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-slate-400">No daily data</div>
                    )}
                  </CardContent>
                </Card>

                {/* Clicks Over Time */}
                <Card>
                  <CardHeader>
                    <CardTitle style={{ color: NAV_DARK_COLOR }}>Daily Clicks</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dailyData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dailyData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: NAV_DARK_COLOR }} angle={-45} textAnchor="end" height={60} />
                          <YAxis tick={{ fontSize: 12, fill: NAV_DARK_COLOR }} />
                          <Tooltip contentStyle={{ color: NAV_DARK_COLOR }} />
                          <Bar dataKey="clicks" fill={chartColors?.[1] || "#0F172A"} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-slate-400">No daily data</div>
                    )}
                  </CardContent>
                </Card>

                {/* Campaign Spend Distribution */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle style={{ color: NAV_DARK_COLOR }}>Campaign Spend Distribution ({displayCurrency})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={campaigns.slice(0, 8).map(c => ({ ...c, cost: convertCurrency(c.cost) }))}
                          dataKey="cost"
                          nameKey="campaign"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ campaign, percent }) => `${campaign?.substring(0, 15)}... (${(percent * 100).toFixed(0)}%)`}
                          labelLine={{ stroke: NAV_DARK_COLOR }}
                        >
                          {campaigns.slice(0, 8).map((_, idx) => (
                            <Cell key={idx} fill={chartColors?.[idx % chartColors.length] || `hsl(${idx * 45}, 70%, 50%)`} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ color: NAV_DARK_COLOR }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="campaigns">
              <Card>
                <CardHeader>
                  <CardTitle style={{ color: NAV_DARK_COLOR }}>Campaign Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Campaign</th>
                          <th className="text-right py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Impressions</th>
                          <th className="text-right py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Clicks</th>
                          <th className="text-right py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>CTR</th>
                          <th className="text-right py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Cost</th>
                          <th className="text-right py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Conv.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaigns.map((c, i) => (
                          <tr key={i} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-2">{c.campaign}</td>
                            <td className="text-right py-3 px-2">{formatNumber(c.impressions)}</td>
                            <td className="text-right py-3 px-2">{formatNumber(c.clicks)}</td>
                            <td className="text-right py-3 px-2">{c.ctr?.toFixed(2)}%</td>
                            <td className="text-right py-3 px-2">{formatCurrency(c.cost)}</td>
                            <td className="text-right py-3 px-2">{c.conversions?.toFixed(0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="keywords">
              <Card>
                <CardHeader>
                  <CardTitle style={{ color: NAV_DARK_COLOR }}>Keyword Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Keyword</th>
                          <th className="text-left py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Campaign</th>
                          <th className="text-right py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Impressions</th>
                          <th className="text-right py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Clicks</th>
                          <th className="text-right py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>CTR</th>
                          <th className="text-right py-3 px-2 font-medium" style={{ color: NAV_DARK_COLOR }}>Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keywords.slice(0, 50).map((k, i) => (
                          <tr key={i} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-2 max-w-xs truncate">{k.keyword}</td>
                            <td className="py-3 px-2">{k.campaign}</td>
                            <td className="text-right py-3 px-2">{formatNumber(k.impressions)}</td>
                            <td className="text-right py-3 px-2">{formatNumber(k.clicks)}</td>
                            <td className="text-right py-3 px-2">{k.ctr?.toFixed(2)}%</td>
                            <td className="text-right py-3 px-2">{formatCurrency(k.cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {keywords.length > 50 && (
                      <p className="text-center text-slate-400 text-sm mt-4">
                        Showing top 50 of {keywords.length} keywords
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Targeted Keywords Tab */}
            <TabsContent value="targeted">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ color: NAV_DARK_COLOR }}>
                    <Target className="w-5 h-5" />
                    Targeted Keywords by Campaign
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingTargeted ? (
                    <div className="text-center py-8 text-slate-500">Loading targeted keywords...</div>
                  ) : !targetedKeywords?.total_keywords ? (
                    <div className="text-center py-12 text-slate-500">
                      <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium">No targeted keywords uploaded yet</p>
                      <p className="text-sm mt-1">Upload targeted keywords from the Branding Admin settings page</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {targetedKeywords.campaigns?.map((campaign, idx) => (
                        <div key={idx} className="border rounded-lg overflow-hidden">
                          <div className="bg-slate-50 px-4 py-3 border-b flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Target className="w-4 h-4 text-primary" />
                              <span className="font-semibold text-slate-800">
                                {isPresentationMode ? `Campaign ${idx + 1}` : campaign}
                              </span>
                            </div>
                            <span className="text-sm text-slate-500">
                              {targetedKeywords.keywords_by_campaign?.[campaign]?.length || 0} keywords
                            </span>
                          </div>
                          <div className="p-4">
                            <div className="flex flex-wrap gap-2">
                              {targetedKeywords.keywords_by_campaign?.[campaign]?.map((kw, kwIdx) => (
                                <span 
                                  key={kwIdx}
                                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-primary/10 text-primary border border-primary/20"
                                >
                                  {isPresentationMode ? `Keyword ${kwIdx + 1}` : kw.keyword}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Summary Card */}
                      <div className="mt-6 p-4 bg-slate-50 rounded-lg border">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-slate-500">Total Campaigns:</span>
                            <span className="ml-2 font-semibold">{targetedKeywords.campaigns?.length || 0}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Total Keywords:</span>
                            <span className="ml-2 font-semibold">{targetedKeywords.total_keywords || 0}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Avg per Campaign:</span>
                            <span className="ml-2 font-semibold">
                              {targetedKeywords.campaigns?.length 
                                ? Math.round(targetedKeywords.total_keywords / targetedKeywords.campaigns.length)
                                : 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default GoogleAdsPage;
